'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { formatPropertyDate } from '@/lib/dates';
import {
  Dialog,
  Text,
  Drawer,
  Button,
  VStack,
  HStack,
  Icon,
  Box,
} from '@ui-library';
import { Link } from '@chakra-ui/react';
import {
  ArrowRight,
  Ban,
  Bed,
  CalendarDays,
  Pencil,
  Trash2,
  User,
} from '@ui-library/icons';

import { LDFeatureFlags } from '@/constants/featureFlags';
import useLocalization from '@/hooks/useLocalization';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  CheckInSettings,
  ExternalOrder,
  ReservationRoomDTO,
  ReservationStatus,
  ReservationWithRateDetailsDTO,
  Settings,
} from '@/models';
import { ClientCookiesStore } from '@/services/cookies/client';
import { GuestPortalService } from '@/services/GuestPortal';

import { getLongDateFormat } from '@/lib/dateFormat';
import { getDateInterval, getRoomTitle } from '../../helper/reservationHelper';
import { CancellationPolicyCard } from './CancellationPolicyCard';
import { CancellationPolicyRow } from './CancellationPolicyRow';
import { CancellationPolicySheet } from './CancellationPolicySheet';

type EditReservationProps = {
  isOpen: boolean;
  onClose: (isCancelledReservation: boolean) => void;
  checkInSettings: CheckInSettings | null;
  reservations: ReservationWithRateDetailsDTO[];
  settings: Settings;
};

export const EditReservation: React.FC<EditReservationProps> = ({
  isOpen,
  onClose,
  checkInSettings,
  reservations,
  settings,
}) => {
  const intl = useIntl();
  const { isEnabled } = useFeatureFlags();

  const { dateFnsLocale } = useLocalization();
  const searchParams = useSearchParams();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openPolicySheet, setOpenPolicySheet] = useState(false);
  const [isCurrentlyCanceling, setIsCurrentlyCanceling] = useState(false);
  const [externalOrders, setExternalOrders] = useState<ExternalOrder[]>([]);
  const [externalOrdersLoading, setExternalOrdersLoading] = useState(true);

  const reservationId = searchParams.get('reservationId') || null;

  const reservation = reservations.find(
    (eachReservation) => eachReservation.id === reservationId,
  );
  const propertyTimezone = settings.whistle_guest_company_timezone;

  const hasThirdPartyIdentifier = !!reservation?.thirdPartyIdentifier;

  const isEditingAllowed = !hasThirdPartyIdentifier;

  const editingDisabledMessage = hasThirdPartyIdentifier
    ? intl.formatMessage(
        {
          id: 'edit_reservation/reservation_details/editing_disabled_third_party_message',
          defaultMessage:
            'Please contact {sourceName} to make changes to your reservation.',
        },
        {
          sourceName: reservation?.sourceName,
        },
      )
    : null;

  const isCancelReservationFeatureEnabled = isEnabled(
    LDFeatureFlags.CancelReservation,
  );
  const hasExternalOrders = externalOrders.length > 0;
  const externalOrderProviderUrl = hasExternalOrders
    ? externalOrders[0].providerUrl
    : undefined;

  const handleReservationCancel = async () => {
    const reservationService = new GuestPortalService(ClientCookiesStore);
    setOpenCancelDialog(false);
    setIsCurrentlyCanceling(true);

    if (!reservation) return;

    const updateResponse = await reservationService.updateReservation(
      reservation.propertyId,
      reservation.id.toString(),
      {
        status: ReservationStatus.Canceled,
      },
    );
    if (
      updateResponse &&
      updateResponse.status === ReservationStatus.Canceled
    ) {
      onClose(true);
    }
    setIsCurrentlyCanceling(false);
  };

  const dateInterval =
    reservation &&
    getDateInterval(
      reservation,
      propertyTimezone,
      dateFnsLocale,
      settings.date_format,
    );

  const getRoomDates = (room: ReservationRoomDTO) => {
    const longFormat = settings.date_format
      ? getLongDateFormat(settings.date_format)
      : 'P';

    return {
      arrival: formatPropertyDate(
        room.roomCheckIn,
        propertyTimezone,
        longFormat,
        dateFnsLocale,
      ),
      departure: formatPropertyDate(
        room.roomCheckOut,
        propertyTimezone,
        longFormat,
        dateFnsLocale,
      ),
    };
  };

  const fetchExternalOrders = async (
    propertyId: string,
    reservationId: string,
  ) => {
    if (!propertyId || !reservationId) return;

    setExternalOrdersLoading(true);

    const reservationService = new GuestPortalService(ClientCookiesStore);
    try {
      const response = await reservationService.getReservationExternalOrders(
        propertyId,
        reservationId,
      );

      if (!response || !response.orders) {
        setExternalOrders([]);
      } else {
        setExternalOrders(response.orders);
      }
      setExternalOrdersLoading(false);
    } catch (error) {
      console.error('Error fetching external orders:', error);
      setExternalOrders([]);
      setExternalOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && reservationId && reservation) {
      fetchExternalOrders(reservation.propertyId, reservationId);
    }
  }, [isOpen, reservationId, reservation]);

  return (
    <>
      <Drawer
        title={intl.formatMessage({
          id: 'edit_reservation/reservation_details/drawer_title',
          defaultMessage: 'Reservation details',
        })}
        titleIcon={<Pencil />}
        size={'sm'}
        closeOnInteractOutside
        open={isOpen}
        onOpenChange={(e) => {
          if (!e.open) {
            onClose(false);
            setOpenPolicySheet(false);
          }
        }}
        trigger={
          <Button variant="ghost" color={'lightning.900'}>
            <Icon size="sm">
              <Pencil />
            </Icon>
          </Button>
        }
      >
        {reservation && dateInterval && (
          <>
            <VStack gap={'4'} alignItems={'flex-start'} w={'full'}>
              {!isEditingAllowed && editingDisabledMessage && (
                <Box
                  borderRadius={'md'}
                  border={'solid 1px'}
                  borderColor={'fog.600'}
                  bg={'fog.200'}
                  p={'4'}
                  w={'full'}
                >
                  <HStack gap={2} alignItems={'flex-start'}>
                    <Icon color="fog.800">
                      <Ban size={20} />
                    </Icon>
                    <VStack gap={0} alignItems={'flex-start'}>
                      <Text fontWeight={'semibold'} color="fog.800">
                        {intl.formatMessage({
                          id: 'edit_reservation/reservation_details/editing_disabled_title',
                          defaultMessage: 'Editing Disabled',
                        })}
                      </Text>
                      <Text fontSize={'xs'} color="fog.800">
                        {editingDisabledMessage}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              )}

              <HStack gap={'4'} alignItems={'flex-start'}>
                <CalendarDays size={20} />
                <VStack alignItems={'flex-start'} gap={0}>
                  <Text fontWeight={'bold'}>
                    {intl.formatMessage({
                      id: 'edit_reservation/reservation_details/dates_label',
                      defaultMessage: 'Dates',
                    })}
                  </Text>
                  <Text>
                    {dateInterval.arrivalDate} - {dateInterval.departureDate}
                  </Text>
                </VStack>
              </HStack>
              <HStack gap={'4'} alignItems={'flex-start'}>
                <Bed size={20} />
                <VStack alignItems={'flex-start'} gap={0}>
                  <Text fontWeight={'bold'}>
                    {intl.formatMessage({
                      id: 'edit_reservation/reservation_details/accommodation_type_label',
                      defaultMessage: 'Accommodation type',
                    })}
                  </Text>
                  <Text>
                    {intl.formatMessage(
                      {
                        id: 'edit_reservation/reservation_details/accommodation_type',
                        defaultMessage: '{accommodationCount} accommodations',
                      },
                      {
                        accommodationCount: reservation?.rooms?.length,
                      },
                    )}
                  </Text>
                  <Box mt={4}>
                    <VStack alignItems={'flex-start'} gap={2}>
                      {reservation?.rooms?.map((room, index) => {
                        const { arrival, departure } = getRoomDates(room);
                        const roomTitle = getRoomTitle(
                          room,
                          intl,
                          checkInSettings?.display_guest_room_number ?? false,
                        );
                        return (
                          <VStack alignItems={'flex-start'} gap={0} key={index}>
                            <Text fontWeight={'bold'}>{roomTitle}</Text>
                            <HStack>
                              {arrival && <Text>{arrival}</Text>}
                              <ArrowRight size={15} />
                              {departure && <Text>{departure}</Text>}
                            </HStack>
                          </VStack>
                        );
                      })}
                    </VStack>
                  </Box>
                </VStack>
              </HStack>
              <HStack gap={'4'} alignItems={'flex-start'}>
                <User size={20} />
                <VStack alignItems={'flex-start'} gap={0}>
                  <Text fontWeight={'bold'}>
                    {intl.formatMessage({
                      id: 'edit_reservation/reservation_details/guests_label',
                      defaultMessage: 'Guests',
                    })}
                  </Text>
                  <Text>
                    {intl.formatMessage(
                      {
                        id: 'edit_reservation/reservation_details/guests_count',
                        defaultMessage: '{adults} adults , {children} children',
                      },
                      {
                        adults: Number(reservation?.adults),
                        children: Number(reservation?.children),
                      },
                    )}
                  </Text>
                </VStack>
              </HStack>
              <CancellationPolicyRow
                reservation={reservation}
                isOpen={isOpen}
                propertyTimezone={propertyTimezone}
                onOpen={() => setOpenPolicySheet(true)}
              />
            </VStack>
            {isEditingAllowed &&
              !['canceled', 'no_show', 'checked_out', 'checked_in'].includes(
                reservation.status! as ReservationStatus,
              ) &&
              (hasExternalOrders && externalOrderProviderUrl ? (
                <Link
                  as="a"
                  href={externalOrderProviderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  mt={'4'}
                  w={'full'}
                >
                  <Button variant={'plain'} colorPalette={'error'} w={'full'}>
                    <Trash2 />{' '}
                    {intl.formatMessage({
                      id: 'edit_reservation/reservation_details/cancel_reservation_button_label',
                      defaultMessage: 'Cancel reservation',
                    })}
                  </Button>
                </Link>
              ) : (
                isCancelReservationFeatureEnabled && (
                  <Dialog
                    placement={{
                      base: 'bottom',
                      sm: 'bottom',
                      md: 'center',
                      lg: 'center',
                    }}
                    variant="error"
                    title="Cancel reservation"
                    titleIcon={<Trash2 />}
                    size={'md'}
                    open={openCancelDialog}
                    onOpenChange={(e) => setOpenCancelDialog(e.open)}
                    cancelAction={() => setOpenCancelDialog(false)}
                    cancelText={intl.formatMessage({
                      id: 'edit_reservation/reservation_details/cancel_reservation_modal_goBack_button_label',
                      defaultMessage: 'Go back',
                    })}
                    saveText={intl.formatMessage({
                      id: 'edit_reservation/reservation_details/cancel_reservation_modal_confirm_cancel_button_label',
                      defaultMessage: 'Cancel reservation',
                    })}
                    saveAction={handleReservationCancel}
                    trigger={
                      <Button
                        variant={'plain'}
                        colorPalette={'error'}
                        mt={'4'}
                        w={'full'}
                        onClick={() => setOpenCancelDialog(false)}
                        loading={isCurrentlyCanceling || externalOrdersLoading}
                        loadingText={
                          isCurrentlyCanceling
                            ? intl.formatMessage({
                                id: 'edit_reservation/reservation_details/cancel_reservation_button_loading_text',
                                defaultMessage: 'Cancelling reservation',
                              })
                            : intl.formatMessage({
                                id: 'edit_reservation/reservation_details/cancel_reservation_button_label',
                                defaultMessage: 'Cancel reservation',
                              })
                        }
                      >
                        <Trash2 />{' '}
                        {intl.formatMessage({
                          id: 'edit_reservation/reservation_details/cancel_reservation_button_label',
                          defaultMessage: 'Cancel reservation',
                        })}
                      </Button>
                    }
                  >
                    <Text>
                      {intl.formatMessage({
                        id: 'edit_reservation/reservation_details/cancel_reservation_message',
                        defaultMessage:
                          ' This action cannot be undone. You will need to book a new reservation if you wish to stay again.',
                      })}
                    </Text>
                    <CancellationPolicyCard
                      reservation={reservation}
                      isOpen={openCancelDialog}
                    />
                  </Dialog>
                )
              ))}
          </>
        )}
      </Drawer>
      {reservation && (
        <CancellationPolicySheet
          reservation={reservation}
          open={openPolicySheet}
          onClose={() => setOpenPolicySheet(false)}
          propertyTimezone={propertyTimezone}
          propertyDateFormat={settings.date_format ?? undefined}
        />
      )}
    </>
  );
};
